import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  getRevisionApprovers, previewRevision, raiseRevision, uploadRevisionDocument,
} from '../services/scheduleService';
import useUIStore from '../store/uiStore';
import { formatDateDDMMYY } from '../utils/formatDate';
import { DELAY_CAUSES, formatSlip } from '../constants/schedule';

// Raise a timeline revision (layout approved 2026-10-25). Every edit is
// re-previewed by the backend, which moves later steps along with it -
// the same calculation that's stored when the revision is sent.
const RaiseRevisionPage = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const showToast = useUIStore((s) => s.showToast);
  const [fromForecast, setFromForecast] = useState(true);
  const [changes, setChanges] = useState({}); // step_id -> { end } | { duration_days }
  const [preview, setPreview] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [form, setForm] = useState({ cause: '', approver_id: '', reason: '' });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getRevisionApprovers(projectId)
      .then((res) => {
        setApprovers(res.data.approvers);
        if (res.data.approvers.length === 1) setForm((f) => ({ ...f, approver_id: String(res.data.approvers[0].id) }));
      })
      .catch(() => {});
  }, [projectId]);

  useEffect(() => {
    const payload = { from_forecast: fromForecast, changes: Object.entries(changes).map(([step_id, c]) => ({ step_id: Number(step_id), ...c })) };
    previewRevision(projectId, payload)
      .then((res) => { setPreview(res.data); setError(''); })
      .catch((err) => setError(err.response?.data?.detail || 'Failed to calculate the new dates'));
  }, [projectId, fromForecast, changes]);

  const change = (stepId, patch) => setChanges((c) => ({ ...c, [stepId]: patch }));

  const send = async () => {
    if (!form.cause) return setError('Choose a cause');
    if (preview?.needs_approval && !form.approver_id) return setError('Choose an approver');
    if (!form.reason.trim()) return setError('Add a reason');
    if (file && file.size > 5 * 1024 * 1024) return setError('The document is larger than 5 MB');
    setSending(true);
    try {
      const res = await raiseRevision(projectId, {
        from_forecast: fromForecast,
        changes: Object.entries(changes).map(([step_id, c]) => ({ step_id: Number(step_id), ...c })),
        reason: form.reason, cause: form.cause, approver_id: form.approver_id ? Number(form.approver_id) : null,
      });
      if (file) {
        try {
          await uploadRevisionDocument(projectId, res.data.revision_id, file);
        } catch (err) {
          showToast(`Revision sent, but the document didn't upload: ${err.response?.data?.detail || 'error'}`, 'error');
        }
      }
      showToast(res.data.message, 'success');
      navigate(`/projects/${projectId}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send the revision');
    } finally {
      setSending(false);
    }
  };

  const label = 'block text-xs text-gray-500 mb-1';
  const input = 'w-full border border-gray-300 rounded px-2 py-1.5 text-sm';
  const handoverShift = preview?.handover_current && preview?.handover_proposed
    ? Math.round((Date.parse(preview.handover_proposed) - Date.parse(preview.handover_current)) / 86400000) : 0;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-4xl mx-auto">
        <Link to={`/projects/${projectId}`} className="text-sm text-white/80 hover:underline">&larr; Back to project</Link>
        <div className="glass-card p-5 mt-3">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h1 className="text-lg font-bold text-gray-900">
              {preview ? `${preview.project_name} · propose Rev ${preview.next_rev}` : 'Propose revision'}
            </h1>
            <div className="flex gap-2 text-xs">
              <button onClick={() => { setFromForecast(true); setChanges({}); }}
                className={`px-3 py-1.5 rounded border ${fromForecast ? 'bg-komal-blue text-white border-komal-blue' : 'border-gray-300'}`}>
                Fill from current forecast
              </button>
              <button onClick={() => { setFromForecast(false); setChanges({}); }}
                className={`px-3 py-1.5 rounded border ${!fromForecast ? 'bg-komal-blue text-white border-komal-blue' : 'border-gray-300'}`}>
                Start from Rev {preview?.current_rev ?? ''} dates
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-1 mb-3">
            Completed steps are locked. Change the new end date, or the days, of any open step; later steps move with it.
          </p>
          {preview?.pending && (
            <p className="text-sm text-red-700 mb-3">A revision is already waiting for approval. It has to be approved or rejected first.</p>
          )}

          {preview && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-2">Step</th>
                    <th className="py-2 pr-2">Rev {preview.current_rev} end</th>
                    <th className="py-2 pr-2">Proposed end</th>
                    <th className="py-2 pr-2">Days</th>
                    <th className="py-2">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.steps.map((s) => (
                    <tr key={s.step_id} className={`border-b border-gray-100 ${s.locked ? 'text-gray-400' : ''}`}>
                      <td className="py-1.5 pr-2">{s.seq} {s.name}</td>
                      <td className="py-1.5 pr-2 whitespace-nowrap">{formatDateDDMMYY(s.current_end)}</td>
                      <td className="py-1.5 pr-2">
                        {s.locked ? `Done ${formatDateDDMMYY(s.proposed_end)}` : (
                          <input type="date" className="border border-gray-300 rounded px-2 py-1 text-sm" value={s.proposed_end}
                            onChange={(e) => e.target.value && change(s.step_id, { end: e.target.value })} />
                        )}
                      </td>
                      <td className="py-1.5 pr-2">
                        {s.locked || s.is_milestone ? s.duration_days : (
                          <input type="number" min="1" className="border border-gray-300 rounded px-2 py-1 text-sm w-20"
                            defaultValue={s.duration_days} key={`${s.step_id}-${s.duration_days}`}
                            onBlur={(e) => {
                              const v = Number(e.target.value);
                              if (v >= 1 && v !== s.duration_days) change(s.step_id, { duration_days: v });
                            }} />
                        )}
                      </td>
                      <td className={`py-1.5 whitespace-nowrap ${s.change_days > 0 ? 'text-red-700' : s.change_days < 0 ? 'text-green-700' : 'text-gray-400'}`}>
                        {s.change_days === 0 ? '—' : formatSlip(s.change_days)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-sm mt-3">
                Handover: {formatDateDDMMYY(preview.handover_current)} →{' '}
                <span className={`font-semibold ${handoverShift > 0 ? 'text-red-700' : ''}`}>
                  {formatDateDDMMYY(preview.handover_proposed)} ({formatSlip(handoverShift)})
                </span>
                {preview.contractual_handover_date && <span className="text-gray-500"> · contractual {formatDateDDMMYY(preview.contractual_handover_date)}</span>}
              </p>
              <p className={`text-xs mt-1 ${preview.needs_approval ? 'text-amber-700' : 'text-green-700'}`}>
                {preview.needs_approval
                  ? 'The handover moves, so this goes to the Projects head or the CEO for approval.'
                  : `Handover unchanged: saved straight away as Rev ${preview.next_rev}, recorded as self-approved by you.`}
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-3 mt-4">
            <label>
              <span className={label}>Cause</span>
              <select className={input} value={form.cause} onChange={(e) => { setForm({ ...form, cause: e.target.value }); setError(''); }}>
                <option value="">Select cause</option>
                {DELAY_CAUSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            {preview?.needs_approval !== false && (
              <label>
                <span className={label}>Approver</span>
                <select className={input} value={form.approver_id} onChange={(e) => { setForm({ ...form, approver_id: e.target.value }); setError(''); }}>
                  <option value="">Select approver</option>
                  {approvers.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.label})</option>)}
                </select>
              </label>
            )}
            <label>
              <span className={label}>Supporting document (optional, up to 5 MB)</span>
              <input type="file" className="text-sm w-full" onChange={(e) => setFile(e.target.files[0] || null)} />
            </label>
          </div>
          <label className="block mt-3">
            <span className={label}>Reason</span>
            <textarea className={`${input} min-h-[72px]`} value={form.reason} placeholder="What changed, and the evidence (MoM, email, vendor letter)"
              onChange={(e) => { setForm({ ...form, reason: e.target.value }); setError(''); }} />
          </label>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          <div className="flex justify-end gap-3 mt-4">
            <Link to={`/projects/${projectId}`} className="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50">Cancel</Link>
            <button onClick={send} disabled={sending || !preview || preview.pending}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
              {sending ? 'Saving...' : preview?.needs_approval === false ? `Save as Rev ${preview.next_rev}` : 'Send for approval'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaiseRevisionPage;
